import {Subject} from 'rxjs';

import {Injectable} from '@angular/core';
import {Route, Router, UrlTree} from '@angular/router';

import {BaseCompositeDataResult} from '../dtos/data-result-composite';
import {DataStorageService} from './data-storage.service';
import {DataService} from './data.service';
import {WizardService} from './wizard.service';
import {CockpitKontrolleService} from '../../cockpit/cockpit-shared/cockpit/cockpit-services/cockpit-kontrolle.service';
import {CockpitStandardService} from '../../cockpit/cockpit-shared/cockpit/cockpit-services/cockpit-standard.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyseStarterService {
  private readonly urlFragmentM1 = 'm1';
  private readonly urlFragmentM2 = 'm2';
  private readonly analyzerPathM1 = `/analyzer/${this.urlFragmentM1}`;
  private readonly analyzerPathM2 = `/analyzer/${this.urlFragmentM2}`;
  private readonly resultPathM1 = `/result/${this.urlFragmentM1}`;
  private readonly resultPathM2 = `/result/${this.urlFragmentM2}`;
  private readonly moduleChangedSource = new Subject<boolean | null>();
  // eslint-disable-next-line @typescript-eslint/member-ordering
  moduleChanged = this.moduleChangedSource.asObservable();
  private _moduleSelectionInitialized = false;
  private _moduleSelectionPromise: Promise<void> | null = null;

  constructor(
    private readonly router: Router,
    private readonly storage: DataStorageService,
    private readonly dataService: DataService,
    private readonly wizard: WizardService,
    private readonly cockpitKontrolle: CockpitKontrolleService,
    private readonly cockpitStandard: CockpitStandardService
  ) {}

  // eslint-disable-next-line @typescript-eslint/member-ordering
  private _isForModule2: boolean | null;

  get isForModule2(): boolean | null {
    return this._isForModule2;
  }

  public async setFlagsAndStartAnalysis(isForModule2?: boolean) {
    await this.ensureModuleSelectionInitialized();
    if (this._isForModule2 !== isForModule2) {
      this._isForModule2 = isForModule2;
      await this.resetData();
      await this.storage.storeIsForModule2(isForModule2);
      this.moduleChangedSource.next(this._isForModule2);
    }

    this.router.navigateByUrl(this.getCurrentModuleAnalyzerPath());
  }

  public routeIsForSelectedModule(route: Route): boolean {
    const selectedUrlFragment = this.isForModule2
      ? this.urlFragmentM2
      : this.urlFragmentM1;
    return route.path === selectedUrlFragment;
  }

  public getCurrentModuleAnalyzerUrl(): UrlTree {
    return this.router.parseUrl(this.getCurrentModuleAnalyzerPath());
  }

  public getCurrentModuleResultUrl(): UrlTree {
    return this.router.parseUrl(this.getCurrentModuleResultPath());
  }

  public getCurrentModuleAnalyzerPath(): string {
    return this.isForModule2 ? this.analyzerPathM2 : this.analyzerPathM1;
  }

  public getCurrentModuleResultPath(): string {
    return this.isForModule2 ? this.resultPathM2 : this.resultPathM1;
  }

  private async loadModuleSelection() {
    this._isForModule2 = await this.storage.getIsForModule2();
    this.moduleChangedSource.next(this._isForModule2);
  }

  private async resetData() {
    this.dataService.resetData();
    this.wizard.publishNewResult({} as BaseCompositeDataResult);
    this.cockpitKontrolle.resetSelection();
    this.cockpitStandard.resetSelection();
  }

  private ensureModuleSelectionInitialized(): Promise<void> {
    if (!this._moduleSelectionInitialized) {
      if (!this._moduleSelectionPromise) {
        this._moduleSelectionPromise = this.loadModuleSelection().then(() => {
          this._moduleSelectionInitialized = true;
          this._moduleSelectionPromise = null;
        });
      }
      return this._moduleSelectionPromise;
    }
    return Promise.resolve();
  }
}
